import { Component, registerComponent } from 'js/component.js';
import { Form, ConditionalRule } from '@RockwellAutomation/rockwell-meteor';
import {
    euCountries, countryToStatesMap, regionNotRequiredCountries, regionToCountriesMap, getBreakpointMediaQuery
} from 'js/constants.js';

// ISO-2 → Country name mapping restricted to countries present in the default dropdown.
const ISO_COUNTRY_FALLBACK_MAP = {
    US: 'United States',
    CA: 'Canada',
    GB: 'United Kingdom',
    DE: 'Germany',
    FR: 'France',
    AU: 'Australia',
    JP: 'Japan',
    CN: 'China',
    IN: 'India',
    BR: 'Brazil',
    MX: 'Mexico',
    NL: 'Netherlands',
    ES: 'Spain',
    IT: 'Italy',
    CH: 'Switzerland',
    BE: 'Belgium',
    AT: 'Austria',
    SE: 'Sweden',
    NO: 'Norway',
    DK: 'Denmark',
    FI: 'Finland',
    PL: 'Poland',
    CZ: 'Czech Republic',
    HU: 'Hungary',
    PT: 'Portugal',
    GR: 'Greece',
    IE: 'Ireland',
    LU: 'Luxembourg',
    SI: 'Slovenia',
    SK: 'Slovakia',
    EE: 'Estonia',
    LV: 'Latvia',
    LT: 'Lithuania',
    MT: 'Malta',
    CY: 'Cyprus',
    BG: 'Bulgaria',
    RO: 'Romania',
    HR: 'Croatia',
};

const componentName = 'abm-intent-drawer';
export class ABMIntentDrawer extends Component {
    STORAGE_KEY = 'abmRecos';
    DRAWER_ID = 'abm-intent-drawer';
    CONTAINER_ID = 'abm-recommendations';
    SUCCESS_COOKIE_NAME = 'abm_form_submitted';

    get EXPIRATION_MS() { // hours
        const contextExpiration = this.element?.dataset.contextExpiration;
        return (contextExpiration ? contextExpiration * 60 * 60 * 1000 : null) || 432000000; // 120 hours
    }

    get CONTENT_REFRESH_INTERVAL() { // hours
        const contentRefreshInterval = this.element?.dataset.contentRefreshInterval;
        return (contentRefreshInterval ? contentRefreshInterval * 60 * 60 * 1000 : null) || 259200000; // 72 hours
    }

    get POST_SUBMIT_INTERVAL() { // days
        const postSubmitInterval = this.element?.dataset.postSubmitInterval;
        return (postSubmitInterval ? postSubmitInterval * 24 * 60 * 60 * 1000 : null) || 7776000000; // 90 days
    }

    get USER_INTENT() {
        return JSON.parse(localStorage.getItem('abmUserInterests'))?.userIntent || 'no-intent';
    }

    get ENABLE_DRAWER() {
        return this.element?.dataset.enableDrawer;
    }

    state = {
        userIntent: 'no-intent',
        elqEndpoint: '',
        elqFormName: '',
        elqSiteId: '',
        elqCampaignId: '',
        confirmationEmailIdNa: '',
        confirmationEmailIdEmea: '',
        confirmationEmailIdApac: '',
        confirmationEmailIdLatam: '',
        buyIntentCampaignId: '',
        researchIntentCampaignId: '',
        stateInputType: 'select',
        theme: 'ra', // Default theme set to 'ra'
    };

    // Cookie utility methods
    setCookie(name, value, days) {
        const expires = new Date();
        expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
        document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
    }

    getCookie(name) {
        const nameEQ = `${name}=`;
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i += 1) {
            let c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
        }
        return null;
    }

    hasSuccessfulSubmission() {
        const cookie = this.getCookie(this.SUCCESS_COOKIE_NAME);
        const hasSubmitted = cookie === 'true';
        return hasSubmitted;
    }

    setSuccessfulSubmission() {
        const days = this.POST_SUBMIT_INTERVAL / (24 * 60 * 60 * 1000); // Convert milliseconds to days
        this.setCookie(this.SUCCESS_COOKIE_NAME, 'true', days);
    }

    init() {
        if (!this.ENABLE_DRAWER) return;

        // Check if user has already submitted successfully within the last 90 days
        if (this.hasSuccessfulSubmission()) {
            console.log('User has already submitted successfully within 90 days. Skipping drawer initialization.');
            return;
        }
        this.initFormConfig();
        this.initDrawer();
    }

    initFormConfig() {
        // Get form configuration from modal data attributes (modal is now sibling to drawer)
        const container = this.element.closest('.cmp-abm-intent-drawer-container');
        const modal = container?.querySelector('#modal-abm-contact-us');
        const formContainer = modal?.querySelector('.cmp-abm-contact-form');

        if (formContainer) {
            this.state.elqEndpoint = formContainer.dataset.eloquaEndpoint || '';
        }

        // Get theme from page, fallback to 'ra' if not found
        const pageTheme = document.querySelector('[data-theme]')?.dataset.theme;
        if (pageTheme) {
            this.state.theme = pageTheme;
        } else {
            // Ensure 'ra' is always the fallback theme
            this.state.theme = 'ra';
        }
    }

    initDrawer = () => {
        const cachedString = localStorage.getItem(this.STORAGE_KEY);
        let cached = null;

        try {
            cached = cachedString ? JSON.parse(cachedString) : null;
        } catch (e) {
            console.warn('Failed to parse cached data', e);
            this.clearCache();
        }

        // Initialize user intent from localStorage or determine from interests
        this.state.userIntent = this.USER_INTENT;
        if (this.state.userIntent === 'no-intent') return;

        if (cached && cached.abmRecosExpires > Date.now()) {
            if (cached?.hideDrawer) {
                return;
            }

            this.renderDrawer(cached, true);
            this.applyDrawerIntent(this.USER_INTENT);
        } else if (this.USER_INTENT === 'buy-intent') {
            this.clearCache();
            const bpid = window.getPiraObj?.().bpid;
            const duns = window.getPiraObj?.().duns;
            const emailDomain = window.getPiraObj?.().em.split('@')[1];
            const payload = {
                data: [],
                queue: [],
                abmRecosExpires: Date.now() + this.EXPIRATION_MS,
                hideDrawer: false,
                bpid,
                duns,
                emailDomain,
            };
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(payload));
            this.applyDrawerIntent(this.USER_INTENT);
        } else {
            this.clearCache();
            this.fetchRecommendations();
        }

        this.addEventListeners();
    };

    getUserInterests() {
        try {
            const stored = localStorage.getItem('abmUserInterests');
            if (!stored) return {};
            return JSON.parse(stored);
        } catch {
            return {};
        }
    }

    clearCache() {
        localStorage.removeItem(this.STORAGE_KEY);
    }

    fetchRecommendations() {
        let locale = document.documentElement.lang.toLowerCase() || 'en-us';
        if (window.location.hostname.includes('localhost')) locale = 'en-us';
        const interests = this.getUserInterests();
        const params = new URLSearchParams();
        params.set('locale', locale);

        interests.topic?.forEach((topic) => {
            params.append('topics', topic);
        });

        interests.industry?.forEach((industry) => {
            params.append('industries', industry);
        });

        interests.research_intent?.forEach((intent) => {
            params.append('research_intent', intent);
        });

        fetch(`/bin/rockwell-automation/abm-content-recommendation?${params}`)
            .then((res) => (res.ok ? res.json() : Promise.reject(res)))
            .then((data) => {
                if (data?.recommendations?.length) {
                    this.renderDrawer(data.recommendations);
                    this.applyDrawerIntent(this.USER_INTENT);
                }
            })
            .catch((err) => console.log('Recommendation fetch error:', err));
    }

    applyDrawerIntent(userIntent = this.USER_INTENT) {
        const buyContent = this.element.querySelector('[data-content-type="buy-intent"]');
        const researchContent = this.element.querySelector('[data-content-type="research-intent"]');

        // reset all content to hidden
        buyContent.setAttribute('hidden', '');
        researchContent.setAttribute('hidden', '');

        if (userIntent === 'buy-intent') {
            const buyTitle = this.element.querySelector('.cmp-abm-intent-drawer__buy-intent .cmp-abm-intent-drawer__title');
            const buyMapping = document.getElementById('abm-buy-mapping');
            if (buyMapping && buyTitle) {
                try {
                    const text = buyMapping.textContent?.trim();
                    if (text && text.startsWith('{')) {
                        const buyTitles = JSON.parse(text);
                        const buyIntentType = this.getUserInterests().buy_intent?.[0];
                        const buyDynamicTitle = buyTitles[buyIntentType];
                        if (buyDynamicTitle) {
                            buyTitle.textContent = buyDynamicTitle;
                        }
                    }
                } catch (err) {
                    console.log('Failed to parse buy intent titles:', err);
                }
            }

            buyContent.removeAttribute('hidden');
        } else if (userIntent === 'research-intent') {
            const researchTitle = this.element.querySelector('.cmp-abm-intent-drawer__research-intent .cmp-abm-intent-drawer__title');
            const researchMappingScript = document.getElementById('abm-research-mapping');

            if (researchMappingScript && researchTitle) {
                try {
                    const text = researchMappingScript.textContent?.trim();
                    if (text && text.startsWith('{')) {
                        const researchTitles = JSON.parse(text);
                        const interests = this.getUserInterests() || {};
                        const interestKey = (interests.research_intent && interests.research_intent[0])
                            || (interests.topic && interests.topic[0]);

                        if (interestKey && researchTitles[interestKey]) {
                            researchTitle.textContent = researchTitles[interestKey];
                        }
                    }
                } catch (err) {
                    console.log('Failed to parse research intent titles:', err);
                }
            }
            researchContent.removeAttribute('hidden');
        }

        // Add a small delay to prevent flash effect when transitioning from loading state
        setTimeout(() => {
            this.element.classList.remove('loading');
            this.adjustFloatingButtonPosition();

            const interests = this.getUserInterests();
            const intentFocus = userIntent === 'buy-intent' ? interests.buy_intent?.[0] : interests.research_intent?.[0];
            window.digitalData = window.digitalData || [];
            window.digitalData.push({
                event: 'abmDisplayed',
                abmDisplayedDetail: {
                    intent: userIntent,
                    intent_focus: intentFocus,
                    topics: interests.topic,
                    industries: interests.industry,
                    displayed: true,
                },
            });
        }, 300); // 300ms delay for smoother transition
    }

    renderDrawer(recs, isCached = false) {
        const container = document.getElementById(this.CONTAINER_ID);
        if (!container) return;

        const contactUs = container.querySelector('.cmp-abm-intent-drawer__contact-us');
        if (!contactUs) return;

        const icon = container.dataset.completedIcon || '';
        const finalRecs = isCached ? this.refreshRecommendations(recs) : this.getBestRecommendations(this.removeVisitedRecommendations(recs));

        if (!isCached) this.cacheRecommendations(finalRecs, recs);
        if (!finalRecs || finalRecs.length === 0) return;

        finalRecs.forEach((rec, i) => {
            const wrapper = document.createElement('div');
            wrapper.className = 'cmp-abm-intent-drawer__content-item';
            wrapper.dataset.cardId = i;
            if (rec.completed) wrapper.classList.add('completed');

            wrapper.innerHTML = `
                <div class="cmp-abm-intent-drawer__content-item-icon" aria-hidden="true">
                    <img src="${this.sanitizeHTML(icon)}" alt="" role="presentation"/>
                </div>
                <a href="${this.sanitizeHTML(rec.path)}" class="cmp-abm-intent-drawer__content-item-link" data-link-type="abm-intent" data-link-text="${this.sanitizeHTML(rec.title)}" aria-label="${this.sanitizeHTML(rec.title)}" aria-describedby="tag-${i}">
                    <div id="tag-${i}" class="cmp-abm-intent-drawer__content-item-tag">${this.sanitizeHTML(rec.type)}</div>
                    <div class="cmp-abm-intent-drawer__content-item-title">${this.sanitizeHTML(rec.title)}</div>
                </a>
            `;

            wrapper.addEventListener('click', () => this.markRecommendationComplete(i));
            wrapper.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    wrapper.querySelector('a').click();
                }
            });
            container.insertBefore(wrapper, contactUs);
        });

    }

    refreshRecommendations(cached) {
        const updatedDisplay = [];
        const newCached = { ...cached };

        cached.data.forEach((rec) => {
            const isExpired = !rec.completed && rec.expires < Date.now();

            if (isExpired) {
                newCached.queue.push(rec); // push expired one to end

                const nextItem = newCached.queue.shift();
                if (nextItem) {
                    nextItem.expires = Date.now() + this.CONTENT_REFRESH_INTERVAL;
                    updatedDisplay.push(nextItem);
                } else {
                    updatedDisplay.push(rec); // fallback
                }
            } else {
                updatedDisplay.push(rec);
            }
        });

        // Overwrite and save the new state
        newCached.data = updatedDisplay;
        localStorage.setItem('abmRecos', JSON.stringify(newCached));

        return newCached.data;
    }

    removeVisitedRecommendations(recs) {
        const visited = localStorage.getItem('pira-pz-visited-pages');
        if (!visited) return recs;
        const visitedSet = new Set(visited.split(','));
        return recs.filter((r) => !visitedSet.has(r.path));
    }

    getBestRecommendations(recs) {
        const best = recs.filter((r) => r.bestMatch);
        const result = [...best];
        const filler = recs.filter((r) => !best.includes(r));

        const max = best.length >= 3 ? 5 : 3;
        while (result.length < max && filler.length > 0) {
            result.push(filler.splice(Math.floor(Math.random() * filler.length), 1)[0]);
        }

        return result;
    }

    cacheRecommendations(recs, allRecs) {
        const bpid = window.getPiraObj?.().bpid;
        const duns = window.getPiraObj?.().duns;
        const emailDomain = window.getPiraObj?.().em.split('@')[1];
        const recExpires = Date.now() + this.CONTENT_REFRESH_INTERVAL;
        const queue = this.removeVisitedRecommendations(allRecs);
        const payload = {
            data: recs.map((r) => ({ ...r, completed: false, expires: recExpires })),
            queue: queue.slice(recs.length),
            abmRecosExpires: Date.now() + this.EXPIRATION_MS,
            hideDrawer: false,
            bpid,
            duns,
            emailDomain,
        };
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(payload));
    }

    markRecommendationComplete(i) {
        try {
            const stored = localStorage.getItem(this.STORAGE_KEY);
            const payload = JSON.parse(stored);
            if (payload?.data?.[i]) {
                payload.data[i].completed = true;
                localStorage.setItem(this.STORAGE_KEY, JSON.stringify(payload));

                // Immediately update the visual state
                const container = document.getElementById(this.CONTAINER_ID);
                if (container) {
                    const item = container.querySelector(`[data-card-id="${i}"]`);
                    if (item) {
                        item.classList.add('completed');
                    }
                }
            }
        } catch (e) {
            console.log('Failed to mark recommendation completed', e);
        }
    }

    sanitizeHTML(str) {
        const temp = document.createElement('div');
        temp.textContent = str;
        return temp.innerHTML;
    }

    hideDrawerWithAnimation() {
        // Save the hideDrawer state to localStorage
        try {
            const stored = localStorage.getItem(this.STORAGE_KEY);
            const payload = stored ? JSON.parse(stored) : {};
            payload.hideDrawer = true;
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(payload));
        } catch (err) {
            console.log('Failed to update hideDrawer flag', err);
        }

        // Use requestAnimationFrame to ensure smooth transition
        requestAnimationFrame(() => {
            this.element.classList.add('loading');
            this.adjustFloatingButtonPosition();
        });

        // After animation completes, hide the drawer completely
        setTimeout(() => {
            this.element.style.display = 'none';
        }, 400); // Match the CSS transition duration (0.4s)
    }

    postToEloqua = async (formData) => {
        if (!this.state.elqEndpoint) {
            console.warn('Eloqua endpoint is not set.');
            return;
        }
        try {
            const eloquaRequestUrl = new URL(this.state.elqEndpoint);
            const eloquaPostBody = {};
            Array.from(formData.entries()).forEach(([key, value]) => {
                if (key === '$Telephone--country-code') {
                    return;
                }
                if (key === '$Telephone') {
                    // Prepend the selected country code once. The visible field
                    // keeps only the user-entered number (getFullTelephone is
                    // overridden after init so the engine no longer writes the
                    // combined value back into the field).
                    eloquaPostBody[key] = `${formData.get('$Telephone--country-code')} ${value}`;
                } else if (key === '$PrepopEmailAddress') {
                    eloquaPostBody[key] = value;
                    eloquaPostBody.$EmailAddress = value;
                } else if (key === '$StateorProvince') {
                    if (this.state.stateInputType === 'select') {
                        eloquaPostBody.$StateorProvince = value;
                    }
                } else {
                    eloquaPostBody[key] = value;
                }
            });

            // Check for UTM parameters in query string
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get('utm_campaign')) {
                eloquaPostBody.utm_campaign = urlParams.get('utm_campaign');
            }
            if (urlParams.get('utm_creative')) {
                eloquaPostBody.utm_creative = urlParams.get('utm_creative');
            }
            if (urlParams.get('utm_medium')) {
                eloquaPostBody.utm_medium = urlParams.get('utm_medium');
            }
            if (urlParams.get('utm_placement')) {
                eloquaPostBody.utm_placement = urlParams.get('utm_placement');
            }
            if (urlParams.get('utm_source')) {
                eloquaPostBody.utm_source = urlParams.get('utm_source');
            }
            if (urlParams.get('utm_content')) {
                eloquaPostBody.utm_content = urlParams.get('utm_content');
            }
            if (urlParams.get('utm_term')) {
                eloquaPostBody.utm_term = urlParams.get('utm_term');
            }

            const response = await fetch(eloquaRequestUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
                },
                mode: 'no-cors',
                body: new URLSearchParams(eloquaPostBody),
            });
            const data = await response.text();
            if (!response.ok) {
                console.warn('Eloqua error:', data);
            }

        } catch (error) {
            console.warn('Error posting to Eloqua:', error);

        }
    };

    prePopulateForm = (formData) => {
        const form = this.element.querySelector('.cmp-abm-contact-form');
        if (form) {
            form.querySelector('input[name="$EmailAddress"]').value = formData.get('$EmailAddress');
        }
    };



    addEventListeners() {
        document.addEventListener('click', (e) => {
            const target = e.target.closest('.cmp-abm-intent-drawer__hide-drawer, .cmp-abm-intent-drawer__mobile-hide-drawer');
            if (!target) return;

            this.hideDrawerWithAnimation();
        });

        window.addEventListener('resize', this.adjustFloatingButtonPosition);
        window.addEventListener('load', this.adjustFloatingButtonPosition);

        const accessibileElements = [
            this.element.querySelector('.cmp-abm-intent-drawer__hide-drawer'),
            this.element.querySelector('.cmp-abm-intent-drawer__mobile-hide-drawer'),
        ];

        accessibileElements.forEach((element) => {
            element.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    element.click();
                }
            });
        });

        // Initialize form functionality
        this.initializeFormHandlers();

        const container = this.element.closest('.cmp-abm-intent-drawer-container');

        // Blind-submit for known users whose account already provides every
        // required field. Evaluate at click time (not once at init) so the
        // decision reflects the consent field the engine surfaces for the user's
        // country, and NEVER blind-submit while a GDPR/CASL consent checkbox is
        // applicable and unchecked — those users must explicitly opt in.
        const openFormButtons = container.querySelectorAll('a[href="#modal-abm-contact-us"]');
        openFormButtons.forEach((button) => {
            button.addEventListener('click', () => {
                const missingRequired = [...container.querySelectorAll(
                    'input[data-required], textarea[data-required], select[data-required]',
                )].some((field) => field.value === '');

                // Not eligible for blind submit: let the modal open normally so
                // the user can finish the form and/or tick the consent checkbox.
                if (missingRequired || this.isConsentOutstanding(container)) {
                    return;
                }

                const abmForm = document.querySelector('.cmp-abm-contact-form__form');
                if (!abmForm) return;
                const formTitle = document.querySelector('.cmp-abm-contact-form__title');
                const formDescription = document.querySelector('.cmp-abm-contact-form__description');
                const formContainer = document.querySelector('.cmp-abm-contact-form__form-container');
                const successContainer = document.querySelector('.cmp-abm-contact-form__success-container');
                formTitle.style.display = 'none';
                formDescription.style.display = 'none';
                formContainer.style.display = 'none';
                successContainer.classList.add('show');
                const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
                abmForm.dispatchEvent(submitEvent);
            });
        });
    }

    showSuccessMessage() {
        // Look for modal in the parent container (sibling to drawer)
        const container = this.element.closest('.cmp-abm-intent-drawer-container');
        const modal = container?.querySelector('#modal-abm-contact-us');

        if (!modal) return;

        const formContainer = modal.querySelector('.cmp-abm-contact-form__form-container');
        const successContainer = modal.querySelector('.cmp-abm-contact-form__success-container');
        const modalTitle = modal.querySelector('.cmp-abm-contact-form__title');
        const modalDescription = modal.querySelector('.cmp-abm-contact-form__description');
        const successCloseButton = modal.querySelector('.cmp-abm-contact-form__success-close-button');

        if (formContainer && successContainer) {
            formContainer.style.display = 'none';
            successContainer.classList.add('show'); // Add show class instead of inline style

            // Set overflow hidden on modal container when success appears
            const modalContainer = modal.querySelector('.modal-container__container');
            if (modalContainer) {
                modalContainer.style.overflow = 'hidden';
            }
        }

        if (modalTitle) modalTitle.style.display = 'none';
        if (modalDescription) modalDescription.style.display = 'none';

        // Focus the close button so user can hit Enter to dismiss
        if (successCloseButton) {
            // Small delay to ensure the success container is fully visible
            setTimeout(() => {
                successCloseButton.focus();
            }, 100);
        }
    }

    hideSuccessMessage() {
        // Look for modal in the parent container (sibling to drawer)
        const container = this.element.closest('.cmp-abm-intent-drawer-container');
        const modal = container?.querySelector('#modal-abm-contact-us');

        if (!modal) return;

        const formContainer = modal.querySelector('.cmp-abm-contact-form__form-container');
        const successContainer = modal.querySelector('.cmp-abm-contact-form__success-container');
        const modalTitle = modal.querySelector('.cmp-abm-contact-form__title');
        const modalDescription = modal.querySelector('.cmp-abm-contact-form__description');

        if (formContainer && successContainer) {
            formContainer.style.display = 'block';
            successContainer.classList.remove('show'); // Remove show class instead of inline style

            // Restore overflow on modal container when hiding success
            const modalContainer = modal.querySelector('.modal-container__container');
            if (modalContainer) {
                modalContainer.style.overflow = '';
            }
        }

        if (modalTitle) modalTitle.style.display = 'block';
        if (modalDescription) modalDescription.style.display = 'block';
    }

    // True when a CASL/GDPR consent checkbox is currently applicable (i.e. shown
    // for the selected country by the engine's conditional rule) but not yet
    // checked. These checkboxes are authored non-required (no `data-required`) and
    // both post to `$MarketingOptIn`, so they are invisible to the plain
    // required-field scan; this inspects them directly and gates blind submit.
    isConsentOutstanding(container = this.element.closest('.cmp-abm-intent-drawer-container')) {
        if (!container) return false;
        const hiddenClass = `${this.state.theme}-u-hidden`;
        const consentFields = container.querySelectorAll('.ra-formfield--casl, .ra-formfield--gdpr');
        return [...consentFields].some((field) => {
            const input = field.querySelector('input[type="checkbox"]');
            if (!input) return false;
            const visible = !field.classList.contains(hiddenClass);
            return visible && !input.checked;
        });
    }

    initializeFormHandlers() {
        // Look for modal in the parent container (sibling to drawer)
        const container = this.element.closest('.cmp-abm-intent-drawer-container');
        const modal = container?.querySelector('#modal-abm-contact-us');

        if (!modal) return;

        const form = modal.querySelector('.cmp-abm-contact-form__form');
        if (!form) return;

        // Apply canonical form engine class tags
        const adapterMap = {
            '$EmailAddress': 'email',
            '$FirstName': 'first-name',
            '$LastName': 'last-name',
            '$Company': 'company',
            '$Country': 'country',
            '$Comments': 'comments'
        };

        Object.entries(adapterMap).forEach(([legacyName, canonicalName]) => {
            const input = form.querySelector(`[name="${legacyName}"]`);
            if (input) {
                const wrapper = input.closest(`.ra-formfield`);
                if (wrapper) {
                    wrapper.classList.add(`ra-formfield--${canonicalName}`);
                }
            }
        });

        form.classList.add('ra-form');

        const submitButton = modal.querySelector('.cmp-abm-contact-form__submit-button');
        const countrySelect = form.querySelector('select[name="$Country"]');
        const successCloseButton = modal.querySelector('.cmp-abm-contact-form__success-close-button');

        // Initialize form setup like software-license-form
        this.initializeFormFields(form, countrySelect, null);

        // Success close button handler
        if (successCloseButton) {
            successCloseButton.addEventListener('click', () => {
                // Only close modal (drawer already hidden on submit)
                if (modal) modal.close();
                // Reset form state for next time
                this.hideSuccessMessage();
            });
        }

        const regionRule = new ConditionalRule('country', 'country', (triggerField) => {
            const selectedCountry = triggerField.getValue();
            let selectedRegion = 'NA';
            Object.keys(regionToCountriesMap).forEach((region) => {
                if (regionToCountriesMap[region].includes(selectedCountry)) {
                    selectedRegion = region;
                }
            });

            const regionInput = form.querySelector('input[name="$Region"]');
            const confirmationEmailIdInput = form.querySelector('input[name="$ConfirmationEmailID"]');
            const formNameInput = form.querySelector('input[name="elqFormName"]');

            if (regionInput) regionInput.value = selectedRegion;
            if (formNameInput) formNameInput.value = `GatedContent-${selectedRegion}`;

            const confirmationEmailIds = {
                default: '',
                NA: this.element.dataset.elqConfirmationEmailIdNa || '',
                EMEA: this.element.dataset.elqConfirmationEmailIdEmea || '',
                APAC: this.element.dataset.elqConfirmationEmailIdApac || '',
                LA: this.element.dataset.elqConfirmationEmailIdLatam || '',
            };

            if (confirmationEmailIdInput) {
                confirmationEmailIdInput.value = confirmationEmailIds[selectedRegion] || confirmationEmailIds.default;
            }
        });

        this.formEngine = new Form(form, { customConditionalRules: [regionRule] });
        this.formEngine.init();

        // The Meteor form engine's submit handler rewrites the telephone field
        // to `${countryCode} ${number}` (see getFullTelephone), which both
        // mutates the visible field and prepends the country code to the
        // submitted value. Override it to keep only the user-entered number;
        // the country code remains available in its own dropdown.
        if (typeof this.formEngine.getFullTelephone === 'function') {
            this.formEngine.getFullTelephone = () => this.formEngine.getFormField('telephone')?.getValue() || '';
        }

        this.formEngine.getFormField('casl').hide();
        this.formEngine.getFormField('gdpr').hide();
        this.formEngine.getFormField('email').getInputElement().setAttribute('data-business-email-required', 'true');

        // Field values set programmatically by presetFormFields() don't fire the
        // engine's country conditional rules, so a known user's pre-filled country
        // never surfaces (or enforces) its CASL/GDPR consent checkbox. Trigger the
        // rules once for the current country so the correct consent field, if any,
        // is shown and required before any blind-submit decision is made.
        if (countrySelect && countrySelect.value) {
            countrySelect.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Form submission
        form.addEventListener('submit', async (event) => {
            if (event.defaultPrevented) return;
            event.preventDefault(); // Stop native submission payload post

            submitButton.classList.add('loading');
            const formData = new FormData(form);

            let abmUserInterests = {};
            try {
                abmUserInterests = JSON.parse(localStorage.getItem('abmUserInterests'));
            } catch (e) {
                console.warn('Error parsing abmUserInterests from localStorage', e);
            }
            let buyIntent = [];
            let researchIntent = [];
            if (abmUserInterests && abmUserInterests.buy_intent && abmUserInterests.buy_intent.length && abmUserInterests.buy_intent.length > 0) {
                buyIntent = abmUserInterests.buy_intent.map(intent => `${intent}_buy`);
            }
            if (abmUserInterests && abmUserInterests.research_intent && abmUserInterests.research_intent.length && abmUserInterests.research_intent.length > 0) {
                researchIntent = abmUserInterests.research_intent.map(intent => `${intent}_research`);
            }
            formData.set('$Comments', researchIntent.length >= buyIntent.length ? `${formData.get('$Comments')} - Research intent: ${researchIntent.join(', ')}` : `${formData.get('$Comments')} - Buy intent: ${buyIntent.join(', ')}`);

            // Set URL and region data
            formData.set('$FormURL', window.location.origin + window.location.pathname);
            // Resolve formName reliably from modal hidden input or component state
            const eloquaFormNameInput = form.querySelector('input[name="elqFormName"]');
            const eloquaFormName = eloquaFormNameInput?.value || 'abm-contact-form';
            const formNameInput = form.querySelector('input[name="$FormName"]');
            const formName = formNameInput?.value || 'abm-contact-form';
            try {
                await this.postToEloqua(formData);

                // Set success cookie to prevent drawer from showing again for 90 days
                this.setSuccessfulSubmission();

                // Hide drawer immediately on successful submission
                this.hideDrawerWithAnimation();

                // Show success message in modal and focus close button
                this.showSuccessMessage();

                window.digitalData = window.digitalData || [];
                window.digitalData.push({
                    event: 'formSubmit',
                    formSubmitDetail: {
                        status: 'success',
                        formName,
                        eloquaFormName,
                    },
                });
                localStorage.removeItem('abmRecos');
            } catch (error) {
                console.warn('Form submission error:', error);

                // Trigger analytics event
                window.digitalData = window.digitalData || [];
                window.digitalData.push({
                    event: 'formSubmit',
                    formSubmitDetail: {
                        status: 'error',
                        formName,
                        eloquaFormName,
                        errorMessage: error.message,
                    },
                });
            } finally {
                submitButton.classList.remove('loading');
            }
        });

        // Field validation handlers
        form.querySelectorAll('select').forEach((select) => {
            select.addEventListener('change', () => {
                const isEmpty = select.value === '';

                if (!isEmpty) {
                    select.classList.add('has-value');
                    // RA-theme floating label lifts on the `--has-value` modifier class
                    select.classList.add(`${this.state.theme}-select__select--has-value`);
                    select.classList.remove(`${this.state.theme}-select__select--invalid`);
                    select.closest(`.${this.state.theme}-formfield`).classList.remove(`${this.state.theme}-formfield--invalid`);
                    // Hide error message
                    const errorMessage = select.closest(`.${this.state.theme}-formfield`).querySelector(`.${this.state.theme}-formfield__caption-error`);
                    if (errorMessage) {
                        errorMessage.setAttribute('aria-hidden', 'true');
                    }
                } else {
                    select.classList.remove('has-value');
                    select.classList.remove(`${this.state.theme}-select__select--has-value`);
                }
            });
        });

        form.querySelectorAll('input, textarea').forEach((input) => {
            input.addEventListener('input', () => {
                if (input.value || input.checked) {
                    input.classList.remove(`${this.state.theme}-input__input--invalid`);
                    input.classList.remove(`${this.state.theme}-checkbox__checkbox--invalid`);
                    input.closest(`.${this.state.theme}-formfield`).classList.remove(`${this.state.theme}-formfield--invalid`);
                }
            });
        });
    }

    checkSignInStatus() {
        const loggedInData = {};
        let isSignedIn = false;

        // Check window.userData as fallback
        if (window.userData) {
            isSignedIn = true;
            loggedInData.firstName = window.userData.firstName;
            loggedInData.lastName = window.userData.lastName;
            loggedInData.email = window.userData.email;
            loggedInData.phone = window.userData.phone;
            loggedInData.company = window.userData.company;
            loggedInData.address = window.userData.address;
            loggedInData.city = window.userData.city;
            loggedInData.region = window.userData.region;
            loggedInData.zip = window.userData.zip;
            loggedInData.countryCode = window.userData.countryCode;
            loggedInData.country = window.userData.country;
            loggedInData.bpid = window.userData.bpid;
        } else if (localStorage.getItem('loggedInData')) {
            // Check localStorage as last resort
            try {
                const loginObject = JSON.parse(localStorage.getItem('loggedInData'));
                loggedInData.firstName = loginObject.firstName;
                loggedInData.lastName = loginObject.lastName;
                loggedInData.email = loginObject.email;
                loggedInData.phone = loginObject.phone;
                loggedInData.company = loginObject.company;
                loggedInData.address = loginObject.address;
                loggedInData.city = loginObject.city;
                loggedInData.region = loginObject.region;
                loggedInData.zip = loginObject.zip;
                loggedInData.countryCode = loginObject.countryCode;
                loggedInData.country = loginObject.country;
                loggedInData.bpid = loginObject.bpid;
                isSignedIn = true;
            } catch (e) {
                console.warn('Error parsing loggedInData from localStorage', e);
            }
        }

        return { loggedInData, isSignedIn };
    }

    presetFormFields(form, userData) {
        if (!userData.email) return;

        const fieldMapping = {
            $FirstName: userData.firstName,
            $LastName: userData.lastName,
            $EmailAddress: userData.email,
            $Telephone: userData.phone,
            $Company: userData.company,
            $AddressLine1: userData.address,
            $City: userData.city,
            $StateorProvince: userData.region,
            $Postcode: userData.zip,
            $Country: userData.countryCode || userData.country,
        };

        Object.keys(fieldMapping).forEach((fieldName) => {
            const field = form.querySelector(`[name="${fieldName}"]`);
            const value = fieldMapping[fieldName];
            if (!field || !value) return;

            if (field.tagName === 'SELECT') {
                let finalValue = value;

                // Special handling for Country select: convert ISO-2 to dropdown country name
                if (fieldName === '$Country' && !field.querySelector(`option[value="${value}"]`)) {
                    let mapped = ISO_COUNTRY_FALLBACK_MAP[value];
                    if (!mapped && window.Intl && typeof Intl.DisplayNames === 'function') {
                        try {
                            const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
                            mapped = displayNames.of(value);
                        } catch (e) { /* noop */ }
                    }
                    if (mapped && field.querySelector(`option[value="${mapped}"]`)) {
                        finalValue = mapped;
                    }
                }

                field.value = finalValue;
                if (field.value) {
                    field.classList.add('has-value');
                    field.classList.add(`${this.state.theme}-select__select--has-value`);
                }
            } else {
                field.value = value;
            }
        });

        // Set hidden fields
        const prepopEmailInput = form.querySelector('input[name="$PrepopEmailAddress"]');
        if (prepopEmailInput && userData.email) {
            prepopEmailInput.value = userData.email;
        }

        let abmUserInterests = {};
        try {
            abmUserInterests = JSON.parse(localStorage.getItem('abmUserInterests'));
        } catch (e) {
            console.warn('Error parsing abmUserInterests from localStorage', e);
            return;
        }
        let buyIntent = [];
        let researchIntent = [];
        if (abmUserInterests && abmUserInterests.buy_intent && abmUserInterests.buy_intent.length && abmUserInterests.buy_intent.length > 0) {
            buyIntent = abmUserInterests.buy_intent.map(intent => `${intent}_buy`);
        }
        if (abmUserInterests && abmUserInterests.research_intent && abmUserInterests.research_intent.length && abmUserInterests.research_intent.length > 0) {
            researchIntent = abmUserInterests.research_intent.map(intent => `${intent}_research`);
        }

        this.state.buyIntentCampaignId = this.element.dataset.buyIntentCampaignId || '701Pg00001NPm9NIAT';
        this.state.researchIntentCampaignId = this.element.dataset.researchIntentCampaignId || '701Pg00001NPS9sIAH';

        const campaignIdInput = form.querySelector('input[name="$CampaignId"]');
        const campaignIdInputAlt = form.querySelector('input[name="elqCampaignId"]');
        if (researchIntent.length >= buyIntent.length && researchIntent.length > 0) {
            if (campaignIdInput) {
                campaignIdInput.value = this.state.researchIntentCampaignId;
            }
            if (campaignIdInputAlt) {
                campaignIdInputAlt.value = this.state.researchIntentCampaignId;
            }
        } else if (buyIntent.length > 0) {
            if (campaignIdInput) {
                campaignIdInput.value = this.state.buyIntentCampaignId;
            }
            if (campaignIdInputAlt) {
                campaignIdInputAlt.value = this.state.buyIntentCampaignId;
            }
        }
        const dunsInput = form.querySelector('input[name="$DUNSNumber"]');
        if (abmUserInterests && abmUserInterests.duns) {
            dunsInput.value = abmUserInterests.duns;
        }
    }

    initializeFormFields(form, countrySelect, stateSelect) {
        // Initialize country dropdown with default options (like software-license-form)
        const defaultCountryOptions = this.element.querySelector('.cmp-abm-contact-form-default-options--country');
        if (countrySelect && countrySelect.querySelectorAll('option').length <= 1 && defaultCountryOptions) {
            defaultCountryOptions.querySelectorAll('option').forEach((option) => {
                const newOption = option.cloneNode(true);
                // Preserve empty value for placeholder options so validation treats them as unselected
                const nameAttr = option.getAttribute('name');
                const valAttr = option.getAttribute('value');
                if (nameAttr !== null && nameAttr !== '') {
                    newOption.name = nameAttr;
                    newOption.value = valAttr;
                } else {
                    // Keep value empty for placeholder like "Select Country"
                    newOption.value = '';
                    // Mimic properties of placeholder option (disabled & selected) to align with input-select component behavior
                    newOption.disabled = true;
                    newOption.hidden = true;
                    newOption.selected = true;
                }
                // Ensure text content is preserved
                countrySelect.appendChild(newOption);
            });
            // Remove the default options container after copying (like software-license-form)
            defaultCountryOptions.remove();
        }

        // Check for signed-in user data and prepopulate (after country options exist)
        const { loggedInData, isSignedIn } = this.checkSignInStatus();
        if (isSignedIn && loggedInData) {
            this.presetFormFields(form, loggedInData);
        }

        // Set initial form values
        const urlInput = form.querySelector('input[name="$FormURL"]');
        if (urlInput) {
            urlInput.value = window.location.origin + window.location.pathname;
        }

        // Set has-value class for selects with values
        form.querySelectorAll('select').forEach((select) => {
            if (select.value) {
                select.classList.add('has-value');
                select.classList.add(`${this.state.theme}-select__select--has-value`);
            } else {
                select.classList.remove('has-value');
                select.classList.remove(`${this.state.theme}-select__select--has-value`);
            }
        });
    }

    adjustFloatingButtonPosition = () => {
        const drawer = this.element;
        const chatBot = document.querySelector('.floating-button__container');
        const rafb = document.getElementById('rafb-master-button-container');

        // Check if drawer is hidden (has loading class or display: none)
        const isDrawerHidden = drawer.classList.contains('loading') || drawer.style.display === 'none';

        // Early exit if drawer is hidden and buttons are already in default state
        if (isDrawerHidden) {
            const chatBotNeedsReset = chatBot && chatBot.style.bottom !== '';
            const rafbNeedsReset = rafb && rafb.style.bottom !== '';

            if (!chatBotNeedsReset && !rafbNeedsReset) {
                return; // Nothing to do, avoid unnecessary DOM manipulation
            }

            // Reset to original positions
            if (chatBotNeedsReset) chatBot.style.bottom = '';
            if (rafbNeedsReset) rafb.style.bottom = '';
            return;
        }

        // Drawer is visible, adjust positions
        if (chatBot) {
            // [LEGACY BREAKPOINT: max-width 1280px -> getBreakpointMediaQuery('down', 'large')]
            if (getBreakpointMediaQuery('down', 'large').matches) {
                const newBottom = `${20 + drawer.offsetHeight}px`;
                if (chatBot.style.bottom !== newBottom) {
                    chatBot.style.bottom = newBottom;
                }
            } else if (chatBot.style.bottom !== '') {
                // Remove inline style to let CSS take over on desktop
                chatBot.style.bottom = '';
            }
        }

        if (rafb) {
            let newBottom;

            // Check if drawer and RAFB intersect horizontally
            const drawerRect = drawer.getBoundingClientRect();
            const rafbRect = rafb.getBoundingClientRect();
            const isIntersecting = drawerRect.right > rafbRect.left && drawerRect.left < rafbRect.right;

            // [LEGACY BREAKPOINT: max-width 1280px -> getBreakpointMediaQuery('down', 'large')]
            if (getBreakpointMediaQuery('down', 'large').matches || isIntersecting) {
                newBottom = `calc(5% + ${drawer.offsetHeight}px)`;
            } else if (rafb.style.bottom !== '') {
                // Remove inline style to let CSS take over on desktop
                rafb.style.bottom = '';
                return;
            }

            if (rafb.style.bottom !== newBottom) {
                rafb.style.bottom = newBottom;
            }
        }
    };
}

/**
 * Initialize modal functionality for dynamically injected drawer
 * This replicates the modal-container.js initialization logic for the specific drawer
 */
const initializeModalForDrawer = (drawerElement) => {
    const LINK_PREFIX = '#modal-';

    // Find modal links within the drawer
    const linkEls = drawerElement.querySelectorAll(`[href^="${LINK_PREFIX}"]`);

    // Also check if there's a container with modal as sibling
    const container = drawerElement.closest('.cmp-abm-intent-drawer-container');

    // Add click listeners to modal trigger links
    linkEls.forEach((linkEl) => {
        linkEl.addEventListener('click', (ev) => {

            ev.preventDefault();
            const href = ev.currentTarget.getAttribute('href');

            // First try to find in container, then globally
            let modalEl = container?.querySelector(`dialog${href}`);
            if (!modalEl) {
                modalEl = document.querySelector(`dialog${href}`);
            }

            if (modalEl) {
                // Reset form state when opening modal
                const drawerComponent = window.Component?.instances?.find((instance) => instance.element?.id === 'abm-intent-drawer');
                if (drawerComponent && drawerComponent.hideSuccessMessage) {
                    drawerComponent.hideSuccessMessage();
                }

                modalEl.showModal();
                document.body.classList.add('no-scroll');
                if (modalEl.querySelector('.ra__player')) {
                    window.dispatchEvent(new Event('resize'));
                }

                // Resolve formName reliably from modal hidden input or component state
                const eloquaFormNameInput = modalEl.querySelector('input[name="elqFormName"]');
                const eloquaFormName = eloquaFormNameInput?.value || 'abm-contact-form';
                const formNameInput = modalEl.querySelector('input[name="$FormName"]');
                const formName = formNameInput?.value || 'abm-contact-form';

                window.digitalData = window.digitalData || [];
                window.digitalData.push({
                    event: 'formDisplayed',
                    formDisplayedDetail: {
                        status: 'success',
                        formName,
                        eloquaFormName,
                    },
                });
            } else {
                console.error('Modal not found for href:', href);
            }
        });
    });

    // Find modal dialogs - check both drawer and container (for sibling modal)
    const modalEls = container
        ? container.querySelectorAll('dialog.modal-container__dialog')
        : drawerElement.querySelectorAll('dialog.modal-container__dialog');
    modalEls.forEach((modalEl) => {
        // Close modal event
        modalEl.addEventListener('close', () => {
            window.dispatchEvent(new CustomEvent('pauseVideo'));
            if (window.vidyardEmbed) {
                const { players } = window.VidyardV4;
                players[0].pause();
            }
            document.body.classList.remove('no-scroll');
        });

        // Click backdrop to close
        const backdrop = modalEl.querySelector('.modal-container__backdrop');
        if (backdrop) {
            backdrop.addEventListener('click', () => modalEl.close());
        }

        // Form handling is now managed by the component itself
    });

};

const checkWhitelist = (whitelist = {}, enableWhitelist = false) => {
    // If whitelist is not enabled, show to everyone
    if (!enableWhitelist) {
        console.info('ABM Intent Drawer: blacklist not enabled.');
        return true;
    }

    const pira = window.getPiraObj();
    const { em, domain } = pira;

    // Check if email is whitelisted first (allows Rockwell emails to be whitelisted)
    if (em && whitelist[em] !== undefined) {
        console.info('ABM Intent Drawer: whitelisted email');
        return true;
    }

    // Check that email or domain is not internal Rockwell
    const checkValue = window.userData ? em : domain;
    if (checkValue && (checkValue.includes('rockwellautomation.com') || checkValue.includes('ra.rockwell.com'))) {
        console.info('ABM Intent Drawer: internal email or domain detected');
        return false;
    }

    // Else return true
    return true;
};

const getAbmIntentDrawerConfigPage = (componentHtmlUrl) => {
    const existingDrawer = document.getElementById('abm-intent-drawer');
    if (existingDrawer) {
        if (window.location.hostname.includes('localhost') || window.location.hostname === '127.0.0.1') {
            existingDrawer.dataset.enableDrawer = 'true';
        }
        if (existingDrawer.dataset.enableDrawer === 'false' || !existingDrawer.dataset.enableDrawer) {
            console.log('Existing ABM Intent Drawer is disabled, skipping registration');
            return;
        }
        // Check if user should see the drawer based on whitelist configuration
        const whitelistElement = existingDrawer.querySelector('#abm-whitelist');
        const whitelistConfig = JSON.parse(whitelistElement?.textContent || '{}');
        const isWhitelistEnabled = whitelistElement?.dataset?.enableWhitelist === 'true';

        if (!checkWhitelist(whitelistConfig, isWhitelistEnabled)) {
            console.info('ABM Intent Drawer: Access denied. User does not meet whitelist criteria.');
            return;
        }

        registerComponent(`#${componentName}`, ABMIntentDrawer);
        // Initialize modal functionality for existing drawer
        initializeModalForDrawer(existingDrawer);
        return;
    }

    // Try GET directly, silently fail
    let compURL = componentHtmlUrl;
    const isLocalhost = window.location.hostname.includes('localhost');
    const isAuthor = window.location.hostname.includes('author');
    if (isLocalhost || isAuthor) {
        compURL += '?wcmmode=disabled';
    }
    fetch(compURL)
        .then((res) => {
            if (!res.ok) return null;
            return res.text();
        })
        .catch(() => null) // suppress network or 404 errors
        .then((htmlText) => {
            if (!htmlText) return;

            const tempWrapper = document.createElement('div');
            tempWrapper.innerHTML = htmlText.trim();

            // Look for the container first (which includes both drawer and modal)
            let componentToInject = tempWrapper.querySelector('.cmp-abm-intent-drawer-container');

            // Fallback to just the drawer if container not found (backward compatibility)
            if (!componentToInject) {
                componentToInject = tempWrapper.querySelector('.cmp-abm-intent-drawer');
            }

            if (!componentToInject) {
                console.log('ABM Intent Drawer component not found in fetched HTML');
                return;
            }

            // Check if drawer is enabled (look inside container if needed)
            const drawerElement = componentToInject.classList.contains('cmp-abm-intent-drawer')
                ? componentToInject
                : componentToInject.querySelector('.cmp-abm-intent-drawer');

            if (window.location.hostname.includes('localhost') || window.location.hostname === '127.0.0.1') {
                if (drawerElement) drawerElement.dataset.enableDrawer = 'true';
            }
            if (!drawerElement || drawerElement.dataset.enableDrawer === 'false' || !drawerElement.dataset.enableDrawer) {
                console.log('Fetched ABM Intent Drawer is disabled, skipping injection');
                return;
            }
            // Check if user should see the drawer based on whitelist configuration
            const whitelistElement = drawerElement.querySelector('#abm-whitelist');
            const whitelistConfig = JSON.parse(whitelistElement?.textContent || '{}');
            const isWhitelistEnabled = whitelistElement?.dataset?.enableWhitelist === 'true';

            if (!checkWhitelist(whitelistConfig, isWhitelistEnabled)) {
                console.info('ABM Intent Drawer: Access denied. User does not meet whitelist criteria.');
                return;
            }

            document.body.appendChild(componentToInject);

            requestAnimationFrame(() => {
                const insertedDrawer = document.getElementById('abm-intent-drawer');
                if (insertedDrawer) {
                    registerComponent(`#${componentName}`, ABMIntentDrawer);
                    // Initialize modal functionality for dynamically injected drawer
                    initializeModalForDrawer(insertedDrawer);
                } else {
                    console.log('Drawer not found after injection');
                }
            });
        });
};

function initializeDrawerWhenReady() {
    console.log('ABM Intent Drawer: Initializing');
    // Inside the PDP preview iframe (previewPdpIframe=true) we don't want the drawer at
    // all — bail before fetching the config page so the request never fires (rather than
    // loading it and hiding the result), mirroring how the header/footer are suppressed.
    if (new URLSearchParams(window.location.search).get('previewPdpIframe') === 'true') {
        console.log('ABM Intent Drawer: skipped in PDP preview iframe');
        return;
    }
    const configPagePath = document.querySelector('.abm-intent-drawer-config')?.getAttribute('data-path');
    if (!configPagePath) return;
    if (window.location.hostname.includes('localhost') || window.location.hostname === '127.0.0.1') {
        const bpid = window.getPiraObj?.().bpid;
        localStorage.setItem('abmUserInterests', JSON.stringify({ userIntent: 'buy-intent', buy_intent: ['local-test'], bpid }));
    }

    const isValidIntent = JSON.parse(localStorage.getItem('abmUserInterests'))?.userIntent !== 'no-intent';

    if (isValidIntent) {
        console.log('ABM Intent Drawer: Initializing (valid intent) and fetching config page');
        getAbmIntentDrawerConfigPage(configPagePath);
    }
}

// Utility function to get cookie (used outside of class)
function getCookie(name) {
    const nameEQ = `${name}=`;
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i += 1) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
}

window.addEventListener('abmUserInterestsReady', () => {
    const successCookie = getCookie('abm_form_submitted');
    if (successCookie === 'true') {
        console.log('ABM Intent Drawer: User has already submitted successfully. Skipping initialization.');
        return;
    }

    initializeDrawerWhenReady();
});
